import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { productsAPI, analyticsAPI, getImageUrl } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs } from 'swiper/modules';
import {
  Package, ShoppingCart, ArrowLeft, ChevronRight,
  MessageCircle, Minus, Plus, Tag, Building2, Box
} from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

/* ── Spec table ───────────────────────────────────────────── */
const SpecTable = ({ rows, introText }) => (
  <div>
    {introText && (
      <p className="text-slate-600 text-sm leading-relaxed mb-4 pb-3 border-b border-slate-100">
        {introText}
      </p>
    )}
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div
        className="grid grid-cols-2 px-4 py-2.5"
        style={{ background: 'linear-gradient(90deg, #8acb95 0%, #6db87a 100%)' }}
      >
        <span className="text-xs font-bold uppercase tracking-widest text-white">Specification</span>
        <span className="text-xs font-bold uppercase tracking-widest text-white">Details</span>
      </div>
      {rows.map((row, index) => {
        const parts = row.value.split(/\s*[•·]\s*/).map(p => p.trim()).filter(Boolean);
        const hasBullets = parts.length > 1;
        return (
          <div
            key={index}
            className="grid grid-cols-2 border-t border-slate-100"
            style={{ background: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}
          >
            <div className="flex items-start gap-2 px-4 py-3 border-r border-slate-100">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[5px] bg-[#8acb95]" />
              <span className="text-sm font-semibold text-slate-700">{row.label}</span>
            </div>
            <div className="px-4 py-3">
              {hasBullets ? (
                <ul className="space-y-1">
                  {parts.map((part, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-sm text-slate-600">
                      <span className="text-[#8acb95] flex-shrink-0 leading-5 text-xs">▸</span>
                      <span>{part}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-sm text-slate-600">{row.value}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ── Description renderer ─────────────────────────────────── */
const DescriptionRenderer = ({ description }) => {
  if (!description) return null;
  const lines = description.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length > 1) {
    const rows = [], plain = [];
    for (const line of lines) {
      const m = line.match(/^([A-Za-z0-9][A-Za-z0-9\s&\/#×%()@+\-]{1,68}?):\s*(.+)/);
      if (m) rows.push({ label: m[1].trim(), value: m[2].trim() });
      else plain.push(line);
    }
    if (rows.length >= 2) return <SpecTable rows={rows} introText={plain.length ? plain.join(' ') : null} />;
  }
  const bulletCount = (description.match(/•/g) || []).length;
  if (bulletCount >= 2) {
    const chunks = description.split(/\s*•\s*/).map(p => p.trim()).filter(Boolean);
    let introText = chunks[0];
    const hdrMatch = introText.match(/\s*(Specifications?|Features?|Specs?|Overview)\s*$/i);
    if (hdrMatch) introText = introText.slice(0, hdrMatch.index).trim();
    const BULLET_LABEL_RE = /^([A-Za-z0-9][A-Za-z0-9\s&\/#×%()@+\-]{1,50}?):\s*(.+)/;
    const groups = [];
    let current = null;
    for (let i = 1; i < chunks.length; i++) {
      const part = chunks[i];
      const m = part.match(BULLET_LABEL_RE);
      if (m) { if (current) groups.push(current); current = { label: m[1].trim(), items: [m[2].trim()] }; }
      else if (current) current.items.push(part);
      else groups.push({ label: '—', items: [part] });
    }
    if (current) groups.push(current);
    const labeledCount = groups.filter(g => g.label !== '—').length;
    if (labeledCount >= 1) {
      const rows = groups.filter(g => g.items.length > 0).map(g => ({ label: g.label, value: g.items.join(' • ') }));
      return <SpecTable rows={rows} introText={introText || null} />;
    }
  }
  return (
    <p className="text-slate-600 leading-relaxed text-sm" style={{ whiteSpace: 'pre-wrap' }}>
      {description}
    </p>
  );
};

/* ── Main page ────────────────────────────────────────────── */
const ProductDetailPage = () => {
  const { id } = useParams();
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const { addToCart } = useCart();

  useEffect(() => {
    if (id) analyticsAPI.track({ event_type: 'product_view', page: 'product_detail', product_id: id }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { data: productData, isLoading: loading } = useApiQuery(
    ['product', id],
    () => productsAPI.getById(id),
    { enabled: !!id }
  );

  const product = productData?.data;

  const { data: relatedData } = useApiQuery(
    ['relatedProducts', product?.category],
    () => productsAPI.getAll({ category: product?.category, is_active: true, limit: 5 }),
    { enabled: !!product?.category }
  );

  const relatedProducts = relatedData?.data?.data?.filter((p) => p.id !== id).slice(0, 4) || [];

  if (loading) return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-square bg-slate-100 rounded-2xl skeleton" />
          <div className="space-y-4">
            <div className="h-6 bg-slate-200 rounded skeleton w-1/3" />
            <div className="h-8 bg-slate-200 rounded skeleton" />
            <div className="h-8 bg-slate-200 rounded skeleton w-2/3" />
            <div className="h-20 bg-slate-200 rounded skeleton" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-20 text-center">
        <Package className="w-16 h-16 text-slate-200 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Product not found</h2>
        <Link to="/products">
          <button className="bg-[#8acb95] text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-[#7bb885] transition-colors">
            Back to Products
          </button>
        </Link>
      </div>
    </div>
  );

  const hasDiscount = product.discount_rate > 0;
  const finalPrice  = product.price * (1 - product.discount_rate / 100);
  const isInStock   = product.quantity > 0;

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`Hello, I'm interested in "${product.name}" (SKU: ${product.code}). Can you provide more details?`);
    window.open(`https://wa.me/1234567890?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-slate-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-[#8acb95] transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/products" className="hover:text-[#8acb95] transition-colors">Products</Link>
          {product.category && (
            <>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link
                to={`/products?category=${encodeURIComponent(product.category)}`}
                className="hover:text-[#8acb95] transition-colors"
              >
                {product.category}
              </Link>
            </>
          )}
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-slate-600 truncate max-w-[200px]">{product.name}</span>
        </nav>

        {/* ── Product section ───────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12" data-testid="product-detail">

          {/* Images */}
          <div>
            {product.images && product.images.length > 0 ? (
              <div>
                <Swiper
                  modules={[Navigation, Thumbs]}
                  navigation
                  thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                  className="rounded-2xl overflow-hidden mb-3 border border-slate-200 bg-white shadow-sm"
                >
                  {product.images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <div className="aspect-square flex items-center justify-center p-4">
                        <img
                          src={getImageUrl(image)}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                {product.images.length > 1 && (
                  <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={8}
                    slidesPerView={4}
                    watchSlidesProgress
                    className="product-thumbs"
                  >
                    {product.images.map((image, index) => (
                      <SwiperSlide key={index} className="cursor-pointer">
                        <div className="aspect-square bg-white rounded-xl overflow-hidden border-2 border-transparent hover:border-[#8acb95] transition-colors">
                          <img src={getImageUrl(image)} alt={`Thumb ${index + 1}`} className="w-full h-full object-contain p-1" />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </div>
            ) : (
              <div className="aspect-square bg-white rounded-2xl flex items-center justify-center border border-slate-200 shadow-sm">
                <Package className="w-24 h-24 text-slate-200" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col gap-5">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="bg-green-50 text-[#6ab57e] text-xs font-semibold px-3 py-1 rounded-full border border-green-100">
                  {product.category}
                </span>
                {isInStock ? (
                  <span className="bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full border border-green-100">
                    ✓ In Stock
                  </span>
                ) : (
                  <span className="bg-red-50 text-red-600 text-xs font-semibold px-3 py-1 rounded-full border border-red-100">
                    Out of Stock
                  </span>
                )}
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold text-slate-800 leading-snug mb-1"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {product.name}
              </h1>
              <p className="text-xs text-slate-400 uppercase tracking-widest">SKU: {product.code}</p>
            </div>

            {/* Price */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
              {hasDiscount ? (
                <>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-slate-400 line-through text-lg">KWD {product.price.toFixed(3)}</span>
                    <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      -{product.discount_rate}% OFF
                    </span>
                  </div>
                  <p className="text-4xl font-bold text-red-600">KWD {finalPrice.toFixed(3)}</p>
                </>
              ) : (
                <p className="text-4xl font-bold text-[#8acb95]">KWD {product.price.toFixed(3)}</p>
              )}
            </div>

            {/* Meta info */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Building2, label: 'Brand', value: product.brand },
                { icon: Box, label: 'Unit', value: product.unit },
                { icon: Tag, label: 'Category', value: product.category },
              ].filter(r => r.value).map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
                  <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#8acb95]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">{label}</p>
                    <p className="text-sm font-semibold text-slate-700">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quantity + CTA */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-slate-700">Quantity:</span>
                <div className="flex items-center gap-0 border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-14 text-center text-sm font-bold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2.5 hover:bg-slate-50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <button
                className="w-full flex items-center justify-center gap-2.5 bg-[#8acb95] hover:bg-[#7bb885] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl text-base transition-all shadow-sm hover:shadow-md"
                onClick={() => addToCart(product, quantity)}
                disabled={!isInStock}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5" />
                {isInStock ? 'Add to Cart' : 'Out of Stock'}
              </button>

              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center justify-center gap-2.5 bg-white border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold py-3.5 rounded-2xl text-sm transition-all"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Inquire on WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* ── Tabs: Description / Specifications ─────────────── */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-12">
          <div className="flex border-b border-slate-100">
            {['description', 'specifications'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 text-sm font-semibold capitalize transition-all border-b-2 ${
                  activeTab === tab
                    ? 'border-[#8acb95] text-[#8acb95]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="p-6">
            {activeTab === 'description' && (
              product.description ? (
                <p className="text-slate-600 leading-relaxed text-sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {product.description}
                </p>
              ) : (
                <p className="text-slate-400 text-sm">No description available for this product.</p>
              )
            )}
            {activeTab === 'specifications' && (
              product.specifications && product.specifications.length > 0 ? (
                <SpecTable rows={product.specifications} introText={null} />
              ) : product.description ? (
                <DescriptionRenderer description={product.description} />
              ) : (
                <p className="text-slate-400 text-sm">No specifications available.</p>
              )
            )}
          </div>
        </div>

        {/* ── Related Products ───────────────────────────────── */}
        {relatedProducts.length > 0 && (
          <section data-testid="related-products">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Related Products
              </h2>
              <Link
                to={`/products?category=${encodeURIComponent(product.category)}`}
                className="text-sm font-semibold text-[#8acb95] hover:text-[#6ab57e] flex items-center gap-1"
              >
                See all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedProducts.map((rp) => (
                <ProductCard key={rp.id} product={rp} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />
      <WhatsAppButton productName={product?.name} />
    </div>
  );
};

export default ProductDetailPage;
